import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { toast } from 'react-toastify';
import { useLanguage } from '../../context/LanguageContext';

interface AdminProfileData {
  email: string;
  role: string;
  displayName?: string;
  phoneNumber?: string;
  department?: string;
  lastLogin?: Date;
}

const AdminProfile = () => {
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<AdminProfileData>({
    email: '',
    role: 'admin',
    displayName: '',
    phoneNumber: '',
    department: '',
  });

  useEffect(() => {
    if (!currentUser || userData?.role !== 'admin') {
      navigate('/');
      return;
    }

    const fetchAdminProfile = async () => {
      try {
        setLoading(true);
        const docRef = doc(db, 'adminProfiles', currentUser.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setProfile({
            ...data,
            lastLogin: data.lastLogin?.toDate(),
          } as AdminProfileData);
        } else {
          // Initialize with user data if no admin profile exists
          setProfile({
            email: userData.email,
            role: userData.role,
            displayName: userData.username || '',
          });
        }
      } catch (error) {
        console.error('Error fetching admin profile:', error);
        toast.error(t('admin.errorLoadingProfile'));
      } finally {
        setLoading(false);
      }
    };

    fetchAdminProfile();
  }, [currentUser, userData, navigate, t]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    try {
      setSaving(true);
      const docRef = doc(db, 'adminProfiles', currentUser.uid);
      await updateDoc(docRef, {
        ...profile,
        updatedAt: new Date()
      });
      toast.success(t('admin.profileUpdated'));
    } catch (error) {
      console.error('Error updating admin profile:', error);
      toast.error(t('admin.errorUpdatingProfile'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold mb-6">{t('admin.adminProfile')}</h1>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.emailAddress')}
            </label>
            <input
              type="email"
              name="email"
              value={profile.email}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
              disabled
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.role')}
            </label>
            <input
              type="text"
              value={profile.role}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
              disabled
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.displayName')}
            </label>
            <input
              type="text"
              name="displayName"
              value={profile.displayName || ''}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.phoneNumber')}
            </label>
            <input
              type="tel"
              name="phoneNumber"
              value={profile.phoneNumber || ''}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.department')}
            </label>
            <input
              type="text"
              name="department"
              value={profile.department || ''}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600"
            />
          </div>

          {profile.lastLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin.lastLogin')}
              </label>
              <input
                type="text"
                value={profile.lastLogin.toLocaleString()}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                disabled
              />
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-primary-600 text-white py-2 rounded-md font-medium hover:bg-primary-700 transition-colors"
            disabled={saving}
          >
            {saving ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('admin.saving')}
              </span>
            ) : (
              t('admin.saveChanges')
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminProfile;